import React, { useEffect, useState } from "react";
import { useT } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Panel, Btn, Field, Input, Select, Textarea, Modal, DataTable, Plus, toast } from "@/pages/app/kit";
import { StatusBadge } from "@/components/shared";

const ACTIONS = ["settlement_override","verified_grade_change","verified_record_archive","governance_setting_change","conflict_closure","reserve_allocation","role_escalation","feature_flag_change"];

export function ApprovalsPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const { user } = useAuth();
  const isAdmin = ["admin","super_admin"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ action_type: "settlement_override", target_id: "", data: "{}", reason: "" });

  const load = () => api.get("/approvals").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    let data;
    try { data = JSON.parse(f.data || "{}"); } catch { toast.error("Data must be valid JSON"); return; }
    try { await api.post("/approvals", { action_type: f.action_type, target_id: f.target_id, data, reason: f.reason }); toast.success("Request created"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const act = async (id, verb) => { try { await api.post(`/approvals/${id}/${verb}`); toast.success("OK"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const cols = [
    { key: "action_type", label: "Action" },
    { key: "target_id", label: "Target", render: (r) => <span className="font-mono text-xs">{String(r.target_id).slice(0,12)}</span> },
    { key: "reason", label: "Reason", render: (r) => <span className="text-mutedtext">{r.reason}</span> },
    { key: "requested_by_name", label: "Requested by" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "act", label: "", render: (r) => (isAdmin && r.status === "Pending") ? (
      <div className="flex gap-2">
        <button className="text-xs font-semibold text-forest hover:underline" onClick={() => act(r.id, "approve")} data-testid={`approve-req-${r.id}`}>Approve</button>
        <button className="text-xs font-semibold text-crittext hover:underline" onClick={() => act(r.id, "reject")}>Reject</button>
      </div>
    ) : null },
  ];

  return (
    <div>
      <PageTitle title={L("Zatwierdzenia dwuosobowe","Two-person approvals")} subtitle={L("Wnioskodawca nie może zatwierdzić własnej akcji.","The requester cannot approve their own action.")}
        action={<Btn icon={Plus} onClick={() => setOpen(true)} data-testid="add-approval-btn">{L("Nowy wniosek","New request")}</Btn>} />
      <DataTable columns={cols} rows={rows} empty={t("empty")} testid="approvals-table" />
      <Modal open={open} onClose={() => setOpen(false)} title={L("Wniosek o zatwierdzenie","Approval request")}>
        <form onSubmit={create} className="space-y-4">
          <Field label="Action type"><Select value={f.action_type} onChange={(e) => setF({ ...f, action_type: e.target.value })}>{ACTIONS.map((a)=><option key={a}>{a}</option>)}</Select></Field>
          <Field label="Target ID" hint="e.g. settlement id, user id, setting key"><Input value={f.target_id} onChange={(e) => setF({ ...f, target_id: e.target.value })} /></Field>
          <Field label="Data (JSON)" hint='e.g. {"status":"Closed"} or {"role":"coordinator"}'><Textarea rows={2} value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} className="font-mono text-xs" /></Field>
          <Field label="Reason" required><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} required data-testid="approval-reason" /></Field>
          <Btn type="submit">{L("Utwórz wniosek","Create request")}</Btn>
        </form>
      </Modal>
    </div>
  );
}

export function SoilPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const [data, setData] = useState({ disclaimer: "", fields: [] });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ field_name: "", area_ha: "", soil_type: "", ph: "", organic_matter: "", cover_crops: "", agronomist_notes: "" });

  const load = () => api.get("/soil/fields").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async (e) => { e.preventDefault(); try { await api.post("/soil/fields", f); toast.success("OK"); setOpen(false); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  return (
    <div>
      <PageTitle title={L("Gleba i płodozmian (pilotaż)","Soil & crop rotation (pilot)")}
        action={<Btn icon={Plus} onClick={() => setOpen(true)} data-testid="add-field-btn">{L("Dodaj pole","Add field")}</Btn>} />
      <div className="mb-4 rounded-md border border-soil/25 bg-soil/5 p-3 text-sm text-soil" data-testid="soil-disclaimer">
        Do weryfikacji przez agronoma. Nie jest to automatyczna instrukcja uprawy. / For agronomist review. This is not an automatic farming instruction.
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {data.fields.length === 0 && <p className="text-mutedtext">{t("empty")}</p>}
        {data.fields.map((fl) => (
          <Panel key={fl.id} title={fl.data?.field_name || "—"}>
            <dl className="space-y-1 text-sm">
              {Object.entries(fl.data || {}).map(([k, v]) => v && (
                <div key={k} className="flex justify-between gap-4"><dt className="capitalize text-mutedtext">{k.replace(/_/g, " ")}</dt><dd className="text-charcoal">{String(v)}</dd></div>
              ))}
            </dl>
          </Panel>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={L("Rekord pola","Field record")}>
        <form onSubmit={create} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={L("Nazwa pola","Field name")} required><Input value={f.field_name} onChange={set("field_name")} required /></Field>
            <Field label={L("Powierzchnia (ha)","Area (ha)")}><Input value={f.area_ha} onChange={set("area_ha")} /></Field>
            <Field label={L("Typ gleby","Soil type")}><Input value={f.soil_type} onChange={set("soil_type")} /></Field>
            <Field label="pH"><Input value={f.ph} onChange={set("ph")} /></Field>
            <Field label={L("Materia organiczna %","Organic matter %")}><Input value={f.organic_matter} onChange={set("organic_matter")} /></Field>
            <Field label={L("Rośliny okrywowe","Cover crops")}><Input value={f.cover_crops} onChange={set("cover_crops")} /></Field>
          </div>
          <Field label={L("Notatki agronoma","Agronomist notes")}><Textarea rows={2} value={f.agronomist_notes} onChange={set("agronomist_notes")} /></Field>
          <Btn type="submit">{t("cta.create")}</Btn>
        </form>
      </Modal>
    </div>
  );
}
